/**
 * Excel Input Normalization
 * Centralized coercion helpers for Excel add-in functions.
 * Converts Excel values (serial dates, text-formatted numbers, etc.) into canonical forms.
 */

import { logDebug } from "../logging";

// Excel serial date conversion constants
// Excel epoch: January 1, 1900 (serial date 1)
// Unix epoch: January 1, 1970
// Difference: 25569 days (accounting for Excel's 1900 leap year bug)
const EXCEL_TO_UNIX_DAYS = 25569;
const MS_PER_DAY = 86400000;

/**
 * Debug flag for coerceExcelDateToYyyyMmDd logging
 * Defaults to OFF. Set OPTLAB_DEBUG_COERCE_DATE=1 to enable.
 * Safe in browser/Excel runtime (process may be undefined).
 */
const DEBUG_COERCE_DATE =
  (typeof process !== "undefined" &&
    typeof process.env !== "undefined" &&
    process.env.OPTLAB_DEBUG_COERCE_DATE === "1") ||
  false;

/**
 * Coerce Excel date value to YYYY-MM-DD format
 * Supports Excel serial dates, Date objects, and various string formats
 * @param value - Excel date value (serial number, Date object, or string)
 * @returns Canonical ISO date string in YYYY-MM-DD format
 * @throws Error if value cannot be parsed as a valid date
 */
export function coerceExcelDateToYyyyMmDd(value: unknown): string {
  // Debug logging (gated behind OPTLAB_DEBUG_COERCE_DATE=1, defaults OFF)
  if (DEBUG_COERCE_DATE) {
    logDebug("[coerceExcelDateToYyyyMmDd] received type=" + typeof value + " value=" + String(value));
  }

  if (value === null || value === undefined || value === "") {
    throw new Error("Expiration must be a date (cell), YYYY-MM-DD, YYYYMMDD, or M/D/YY.");
  }

  // Handle Excel serial date (number)
  if (typeof value === "number") {
    if (!isFinite(value)) {
      throw new Error("Expiration must be a valid date number.");
    }
    
    // Excel serial date conversion:
    // Excel epoch: Jan 1, 1900 (serial 1)
    // Unix epoch: Jan 1, 1970
    // Formula: (serial - 25569) * 86400 * 1000 = milliseconds since Unix epoch
    // Use UTC to avoid timezone issues
    const unixMs = (value - EXCEL_TO_UNIX_DAYS) * MS_PER_DAY;
    const date = new Date(unixMs);
    
    // Validate the date is reasonable
    if (!isFinite(date.getTime())) {
      throw new Error("Expiration must be a valid date number.");
    }
    
    // Extract UTC components to avoid timezone issues
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    
    // Validate reasonable date range
    if (year < 1900 || year > 2100) {
      throw new Error("Expiration year must be between 1900 and 2100.");
    }
    
    return year + "-" + month + "-" + day;
  }

  // Handle Date objects
  if (value instanceof Date) {
    if (!isFinite(value.getTime())) {
      throw new Error("Expiration must be a valid date.");
    }
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, "0");
    const day = String(value.getUTCDate()).padStart(2, "0");
    return year + "-" + month + "-" + day;
  }

  // Handle strings
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") {
      throw new Error("Expiration must be a date (cell), YYYY-MM-DD, YYYYMMDD, or M/D/YY.");
    }

    // Check if string is a pure number (could be Excel serial date as string)
    // Excel serial dates are typically 1-100000+ (dates from 1900 to 2174+)
    // Can include fractional part for time (e.g., "46044.5" = 2026-01-22 12:00)
    const numericMatch = trimmed.match(/^-?\d+(?:\.\d+)?$/);
    if (numericMatch) {
      const numValue = parseFloat(trimmed);
      // If it's in Excel serial date range (1 to ~100000), treat as serial date
      // Negative values are invalid for dates
      if (numValue >= 1 && numValue <= 100000 && isFinite(numValue)) {
        // Recursively call with the numeric value (will use Math.floor to get just the date part)
        // Excel serial dates with decimals represent date+time, but we only need the date
        return coerceExcelDateToYyyyMmDd(Math.floor(numValue));
      }
    }

    // Try YYYY-MM-DD format
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      validateDateComponents(year, month, day);
      return trimmed;
    }

    // Try YYYYMMDD format
    const compactMatch = trimmed.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (compactMatch) {
      const [, year, month, day] = compactMatch;
      validateDateComponents(year, month, day);
      return year + "-" + month + "-" + day;
    }

    // Try M/D/YY, MM/DD/YY, M/D/YYYY, MM/DD/YYYY formats
    const slashMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (slashMatch) {
      const [, monthStr, dayStr, yearStr] = slashMatch;
      const month = monthStr.padStart(2, "0");
      const day = dayStr.padStart(2, "0");
      
      // Handle two-digit year windowing: 00-79 => 2000-2079, 80-99 => 1980-1999
      let year: string;
      if (yearStr.length === 2) {
        const yearNum = parseInt(yearStr, 10);
        if (yearNum >= 0 && yearNum <= 79) {
          year = "20" + yearStr;
        } else if (yearNum >= 80 && yearNum <= 99) {
          year = "19" + yearStr;
        } else {
          throw new Error("Expiration year must be between 00-99 for two-digit format.");
        }
      } else {
        year = yearStr;
      }
      
      validateDateComponents(year, month, day);
      return year + "-" + month + "-" + day;
    }

    throw new Error("Expiration must be a date (cell), YYYY-MM-DD, YYYYMMDD, or M/D/YY.");
  }

  throw new Error("Expiration must be a date (cell), YYYY-MM-DD, YYYYMMDD, or M/D/YY.");
}

/**
 * Validate date components
 */
function validateDateComponents(year: string, month: string, day: string): void {
  const monthNum = parseInt(month, 10);
  if (monthNum < 1 || monthNum > 12) {
    throw new Error("Month must be between 01 and 12.");
  }

  const dayNum = parseInt(day, 10);
  if (dayNum < 1 || dayNum > 31) {
    throw new Error("Day must be between 01 and 31.");
  }

  const yearNum = parseInt(year, 10);
  if (yearNum < 1900 || yearNum > 2100) {
    throw new Error("Year must be between 1900 and 2100.");
  }
}

/**
 * Coerce Excel number value to number
 * Supports numbers and numeric strings
 * @param value - Excel number value (number or string)
 * @returns Number value
 * @throws Error if value cannot be parsed as a number
 */
export function coerceExcelNumber(value: unknown): number {
  if (typeof value === "number") {
    if (!isFinite(value)) {
      throw new Error("Strike must be a number.");
    }
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") {
      throw new Error("Strike must be a number.");
    }
    
    // Remove commas if present
    const cleaned = trimmed.replace(/,/g, "");
    
    const num = parseFloat(cleaned);
    if (isNaN(num) || !isFinite(num)) {
      throw new Error("Strike must be a number.");
    }
    return num;
  }

  throw new Error("Strike must be a number.");
}

/**
 * Coerce Excel text value to string
 * @param value - Excel text value
 * @param required - If true, throw error for empty strings (default: false)
 * @returns Trimmed string
 * @throws Error if required is true and value is empty
 */
export function coerceExcelText(value: unknown, required: boolean = false): string {
  const result = String(value ?? "").trim();
  if (required && result === "") {
    throw new Error("Value cannot be empty.");
  }
  return result;
}
